// ANGEL FLIGHT — EnemyAssets: shared rigged-character GLB templates.
//
// Loads the CC0 Kenney character GLBs ONCE at startup, then GlbEnemyView clones
// a template per enemy (via SkeletonUtils so the skinned mesh + skeleton clone
// correctly) so spawning stays synchronous. Each clone gets its own
// AnimationMixer driving walk/sprint/die. Until the templates finish loading,
// GlbEnemyView falls back to a capsule rig so the game is never blocked.

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

export interface CharacterTemplate {
  scene: THREE.Group;
  animations: THREE.AnimationClip[];
}

const BASE = import.meta.env.BASE_URL;
/** Distinct character meshes mapped to enemy skins (recolored per type). */
const FILES: Record<string, string> = {
  a: `${BASE}assets/models/characters/character-male-a.glb`,
  c: `${BASE}assets/models/characters/character-male-c.glb`,
};

const templates = new Map<string, CharacterTemplate>();
let loadStarted = false;
let loadDone = false;

/** Kick off the GLB loads (idempotent). Resolves when all templates are ready
 *  (or have failed — failures just leave that key absent). */
export function preloadEnemyAssets(): Promise<void> {
  if (loadStarted) return Promise.resolve();
  loadStarted = true;
  const loader = new GLTFLoader();
  const jobs = Object.entries(FILES).map(
    ([key, url]) =>
      new Promise<void>((resolve) => {
        loader.load(
          url,
          (gltf) => {
            // Make every material instance-safe (clones share geometry but each
            // enemy recolors its own material clone in GlbEnemyView).
            gltf.scene.traverse((o) => {
              const m = o as THREE.Mesh;
              if (m.isMesh) {
                m.castShadow = false;
                m.frustumCulled = false; // skinned + scaled; avoid pop-out
              }
            });
            templates.set(key, { scene: gltf.scene, animations: gltf.animations });
            resolve();
          },
          undefined,
          () => resolve(), // tolerate a failed load — capsule fallback covers it
        );
      }),
  );
  return Promise.all(jobs).then(() => {
    loadDone = true;
  });
}

export function enemyAssetsReady(): boolean {
  return loadDone && templates.size > 0;
}

export function getTemplate(key: string): CharacterTemplate | undefined {
  return templates.get(key) ?? templates.values().next().value;
}

/** All loaded template keys (for round-robin variety). */
export function templateKeys(): string[] {
  return [...templates.keys()];
}
